/**
 * Cart Handlers - Simple, sequential cart checks
 * Uses cart data from events when available to avoid redundant fetches
 * On any cart change: run Nutrafol limit check, then GWP threshold check
 */
(function() {
  'use strict';

  var isRunning = false;
  var debounceTimer = null;
  var weTriggered = false; // Flag to skip self-triggered events
  var pendingCart = null; // Store cart from event for debounced handler
  var gwpActivate = (window.themeCartConfig && window.themeCartConfig.gwpActivate) || false;
  var gwpThreshold = (window.themeCartConfig && window.themeCartConfig.gwpThreshold) || 0;

  /**
   * Main handler - uses cart from event if available, otherwise fetches
   */
  function handleCartChange(cartData) {
    if (isRunning) return;
    isRunning = true;

    // Use cart from event if provided, otherwise fetch
    var cartPromise = cartData
      ? Promise.resolve(cartData)
      : fetch('/cart.js').then(function(r) { return r.json(); });

    cartPromise
      .then(function(cart) {
        return checkNutrafol(cart)
          .then(function(updatedCart) { return checkGWP(updatedCart || cart); });
      })
      .catch(function(err) {
        console.error('[Cart] Error:', err);
      })
      .finally(function() {
        isRunning = false;
      });
  }

  /**
   * 1. Nutrafol - limit to 3 per product
   */
  function checkNutrafol(cart) {
    var needsUpdate = false;
    var updates = {};

    cart.items.forEach(function(item) {
      if (item.vendor === 'Nutrafol' && item.quantity > 3) {
        updates[item.key] = 3;
        needsUpdate = true;
      }
    });

    if (!needsUpdate) {
      return Promise.resolve(cart);
    }

    showMessage('Nutrafol products limited to 3 per order.');

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
    .then(function(r) { return r.json(); });
  }

  /**
   * 2. GWP - check threshold, auto-remove if above
   */
  function checkGWP(cart) {
    if (!gwpActivate) {
      return Promise.resolve(cart);
    }

    var giftItem = null;
    cart.items.forEach(function(item) {
      if (item.properties && item.properties._gift_with_purchase) {
        giftItem = item;
      }
    });

    var subtotal = cart.items_subtotal_price;
    if (giftItem) {
      subtotal -= giftItem.final_line_price;
    }

    var qualifies = subtotal <= gwpThreshold && gwpThreshold > 0;

    var container = document.querySelector('.gwp-upsell-container');
    if (container) {
      container.style.display = (qualifies && cart.item_count > 0) ? 'block' : 'none';
    }

    if (giftItem && !qualifies) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: giftItem.key, quantity: 0 })
      })
      .then(function(r) { return r.json(); })
      .then(function(updatedCart) {
        weTriggered = true;
        document.dispatchEvent(new Event('cart:build'));
        setTimeout(function() { weTriggered = false; }, 200);
        return updatedCart;
      });
    }

    if (giftItem) {
      updateGWPButtons(giftItem.variant_id, true);
    } else if (qualifies) {
      updateGWPButtons(null, false);
    }

    return Promise.resolve(cart);
  }

  /**
   * Update GWP button states
   */
  function updateGWPButtons(selectedVariantId, hasGift) {
    var buttons = document.querySelectorAll('.btn-gwp, .gwp-btn .btn');
    var items = document.querySelectorAll('.gwp-item');

    buttons.forEach(function(btn) {
      if (hasGift) {
        btn.setAttribute('disabled', '');
        btn.textContent = (btn.dataset.gwp_product_id == selectedVariantId) ? 'Added' : 'Add';
      } else {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add';
      }
    });

    items.forEach(function(item) {
      item.classList.remove('selected', 'not_selected');
      if (hasGift) {
        var btn = item.querySelector('.btn-gwp, .gwp-btn .btn');
        if (btn && btn.dataset.gwp_product_id == selectedVariantId) {
          item.classList.add('selected');
        } else {
          item.classList.add('not_selected');
        }
      }
    });
  }

  /**
   * Show a temporary message in cart drawer
   */
  function showMessage(text) {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    var existing = drawer.querySelector('.cart-handler-message');
    if (existing) existing.remove();

    var msg = document.createElement('div');
    msg.className = 'cart-handler-message';
    msg.style.cssText = 'color:#e74c3c;padding:10px;margin:10px;background:#fff3f3;border-radius:4px;text-align:center;';
    msg.textContent = text;

    var inner = drawer.querySelector('.drawer__inner');
    if (inner) {
      inner.insertBefore(msg, inner.firstChild);
      setTimeout(function() { if (msg.parentNode) msg.remove(); }, 5000);
    }
  }

  /**
   * Debounced trigger - skips self-triggered events, uses cart from event
   */
  function debouncedRun(cartData) {
    if (weTriggered) return;
    pendingCart = cartData || pendingCart; // Keep most recent cart data
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function() {
      handleCartChange(pendingCart);
      pendingCart = null;
    }, 750);
  }

  /**
   * Initialize Splide carousel for GWP items
   */
  function initSplide() {
    var splideElement = document.querySelector('.splide-cart-drawer');
    if (splideElement && typeof Splide !== 'undefined' && !splideElement.classList.contains('is-initialized')) {
      try {
        new Splide(splideElement, {
          type: 'slide',
          perPage: 3,
          perMove: 1,
          gap: '1rem',
          pagination: false,
          arrows: true,
          breakpoints: {
            768: { perPage: 2 },
            480: { perPage: 1 }
          }
        }).mount();
        splideElement.classList.add('is-initialized');
      } catch (err) {}
    }
  }

  // Event listeners - extract cart from event.detail when available
  document.addEventListener('cart:build', function(evt) {
    debouncedRun(evt.detail ? evt.detail.cart : null);
  });
  document.addEventListener('cart:updated', function(evt) {
    debouncedRun(evt.detail ? evt.detail.cart : null);
  });

  document.addEventListener('drawerOpen.CartDrawer', function() {
    setTimeout(function() {
      initSplide();
      handleCartChange(null); // No cart data available, will fetch
    }, 100);
  });

  document.addEventListener('DOMContentLoaded', function() {
    initSplide();
    handleCartChange(null); // Initial load, will fetch
  });

  // GWP button click handlers
  document.addEventListener('click', function(e) {
    if (e.target.matches('.btn-gwp') || e.target.matches('.gwp-btn .btn')) {
      e.preventDefault();
      var btn = e.target.closest('.btn-gwp') || e.target.closest('.btn');
      var variantId = btn.dataset.gwp_product_id;
      if (!variantId) return;

      btn.textContent = 'Adding...';
      btn.setAttribute('disabled', '');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { '_gift_with_purchase': 'Gift with purchase!' } }]
        })
      })
      .then(function(r) { return r.json(); })
      .then(function() {
        weTriggered = true;
        document.dispatchEvent(new Event('cart:build'));
        setTimeout(function() { weTriggered = false; }, 200);
      })
      .catch(function() {
        btn.textContent = 'Add';
        btn.removeAttribute('disabled');
      });
    }

    if (e.target.matches('.gwp_remove')) {
      e.preventDefault();
      var giftItem = e.target.closest('.cart-gwp-item') || e.target.closest('[data-gwp_prod]');
      if (!giftItem) return;
      var lineKey = giftItem.dataset.key;
      if (!lineKey) return;

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      })
      .then(function(r) { return r.json(); })
      .then(function() {
        weTriggered = true;
        document.dispatchEvent(new Event('cart:build'));
        setTimeout(function() { weTriggered = false; }, 200);
      });
    }
  });
})();
